import {Component, OnInit} from '@angular/core';
import {ItemDataService} from '../../services/item-data.service';
import {BaseItem, BaseItemRequestPayload} from '@emm/common-items/models/item.model';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {
    CreateCommonItemDialogComponent
} from '@emm/common-items/dialogs/create-common-item-dialog/create-common-item-dialog.component';
import {environment} from '@env';
import {UserService} from '@emm/core/services/user.service';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {NotificationService} from '@emm/shared/services/notification.service';
import {BehaviorSubject, combineLatest, forkJoin} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, shareReplay} from 'rxjs/operators';
import {Modifier} from '@emm/common-items/models/modifier.model';

@Component({
    selector: 'app-items',
    templateUrl: './items.component.html',
    styleUrls: ['./items.component.scss'],
    standalone: false
})
export class CommonItemsListComponent implements OnInit {

    protected readonly environment = environment;
    searchTerm = '';
    filter: 'ALL' | 'ITEM' | 'MODIFIER' | 'ITEM_GROUPING' | 'MODIFIER_GROUPING' | 'MIGRATED' = 'MIGRATED';

    items$ = new BehaviorSubject<BaseItem[]>([]);
    private searchTerm$ = new BehaviorSubject<string>('');
    private filter$ = new BehaviorSubject<string>(this.filter);
    private sortColumn$ = new BehaviorSubject<string | null>(null);
    private sortDirection$ = new BehaviorSubject<'asc' | 'desc'>('asc');

    filteredItems$ = combineLatest([
        this.items$,
        this.searchTerm$.pipe(distinctUntilChanged(), debounceTime(150)),
        this.filter$.pipe(distinctUntilChanged()),
        this.sortColumn$,
        this.sortDirection$
    ]).pipe(
        map(([items, search, filter, sortColumn, sortDirection]) => {
            let filtered = items;
            if (filter !== 'ALL') {
                filtered = filter === 'MIGRATED'
                    ? filtered.filter(item => item.migrated === true)
                    : filtered.filter(item => (item.class === filter || item.objectType === filter));
            }
            const searchNormalized = search.trim().toLowerCase();
            if (searchNormalized) {
                filtered = filtered.filter(item => {
                    const name = item?.name?.toLowerCase() || '';
                    const tag = item?.tag?.toLowerCase() || '';
                    return name.includes(searchNormalized) || tag.includes(searchNormalized);
                });
            }

            if (sortColumn) {
                filtered = [...filtered].sort((a, b) => {
                    const aValue = this.getColumnValue(a, sortColumn);
                    const bValue = this.getColumnValue(b, sortColumn);
                    const comparison = this.compareValues(aValue, bValue);
                    return sortDirection === 'asc' ? comparison : -comparison;
                });
            }

            return filtered;
        }),
        shareReplay(1)
    );

    sortColumn: string | null = null;
    sortDirection: 'asc' | 'desc' = 'asc';

    hideButtonsToggle$ = this.launchDarklyService.getBooleanFlag(FlagKeys.HIDE_BUTTONS);
    showCommonItems$ = this.launchDarklyService.getBooleanFlag(FlagKeys.USE_COMMON_ITEMS);
    isSuperAdmin = false;
    isLoading = true;

    constructor(
        private dialog: MatDialog,
        private itemDataService: ItemDataService,
        private router: Router,
        private launchDarklyService: LaunchDarklyService,
        private userService: UserService,
        private notificationService: NotificationService,
        ) {}

    ngOnInit() {
        this.isSuperAdmin = this.userService.hasSuperAdminRole();
        this.loadAllCommonItems();
    }

    private getItemClass(item: BaseItem): string {
        return item.class || item.objectType || '';
    }

    private getCategoryFromChar(char: string): number {
        const upperChar = char.toUpperCase();
        if (/^[A-Z]$/.test(upperChar)) {
            return 0;
        } else if (/^[0-9]$/.test(upperChar)) {
            return 1;
        } else {
            return 2;
        }
    }

    private getSortInfo(value: string) {
        const firstChar = value.charAt(0);
        const category = this.getCategoryFromChar(firstChar);
        let numericValue: number | null = null;

        if (category === 1) {
            const match = value.match(/^(\d+)/);
            if (match) {
                numericValue = parseInt(match[1], 10);
            }
        }

        return {category, value: value.toUpperCase(), numericValue};
    }

    private getColumnValue(item: BaseItem, column: string): string {
        switch (column) {
            case 'tag':
                return (item.tag || '').trim();
            case 'name':
                return (item.name || '').trim();
            case 'class':
                return this.getItemClass(item).trim();
            case 'type':
                return (item.type || '').trim();
            default:
                return '';
        }
    }

    private compareValues(aValue: string, bValue: string): number {
        const aInfo = this.getSortInfo(aValue);
        const bInfo = this.getSortInfo(bValue);

        if (aInfo.category !== bInfo.category) {
            return aInfo.category - bInfo.category;
        }

        if (aInfo.category === 1 && aInfo.numericValue !== null && bInfo.numericValue !== null) {
            if (aInfo.numericValue !== bInfo.numericValue) {
                return aInfo.numericValue - bInfo.numericValue;
            }
        }

        return aInfo.value.localeCompare(bInfo.value, undefined, {sensitivity: 'base'});
    }

    sortItems(a: BaseItem, b: BaseItem): number {
        const aValue = (a.name && a.name.trim()) ? a.name.trim() : (a.tag || '').trim();
        const bValue = (b.name && b.name.trim()) ? b.name.trim() : (b.tag || '').trim();
        return this.compareValues(aValue, bValue);
    }

    loadAllCommonItems() {
        forkJoin({
            itemGroupsResponse: this.itemDataService.getItemGroups(),
            itemsResponse: this.itemDataService.getItems(),
            modifierGroupsResponse: this.itemDataService.getModifierGroups(),
            modifiersResponse: this.itemDataService.getModifiers()
        }).subscribe({
            next: ({itemGroupsResponse, itemsResponse, modifierGroupsResponse, modifiersResponse}) => {
                this.items$.next(
                    [
                        ...itemGroupsResponse,
                        ...itemsResponse,
                        ...modifierGroupsResponse,
                        ...modifiersResponse
                    ].sort((a, b) => this.sortItems(a, b))
                );
                this.isLoading = false;
            },
            error: () => {
                this.notificationService.error(`Failed to load common items`);
                this.isLoading = false;
            }
        });
    }

    reloadItemsByClass(itemClass: string) {
        this.isLoading = true;
        const currentItems = this.items$.value;
        const reloadConfig: Record<string, {
            method: () => any;
            classTypes: string[];
            errorMessage: string;
        }> = {
            ITEM_GROUPING: {
                method: () => this.itemDataService.getItemGroups(),
                classTypes: ['ITEM_GROUPING'],
                errorMessage: 'Failed to reload items'
            },
            ITEM: {
                method: () => this.itemDataService.getItems(),
                classTypes: ['ITEM'],
                errorMessage: 'Failed to reload items'
            },
            MODIFIER: {
                method: () => this.itemDataService.getModifiers(),
                classTypes: ['MODIFIER'],
                errorMessage: 'Failed to reload modifiers'
            },
            MODIFIER_GROUPING: {
                method: () => this.itemDataService.getModifierGroups(),
                classTypes: ['MODIFIER_GROUPING'],
                errorMessage: 'Failed to reload modifier groups'
            }
        };

        const config = reloadConfig[itemClass];
        if (!config) {
            this.isLoading = false;
            return;
        }

        config.method().subscribe({
            next: (response: BaseItem[]) => {
                const otherItems = currentItems.filter(item =>
                    !config.classTypes.includes(this.getItemClass(item))
                );
                this.items$.next([...response, ...otherItems].sort((a, b) => this.sortItems(a, b)));
                this.isLoading = false;
            },
            error: () => {
                this.notificationService.error(config.errorMessage);
                this.isLoading = false;
            }
        });
    }

    setFilter(filter: typeof this.filter) {
        this.filter = filter;
        this.filter$.next(filter);
    }

    onSearchTermChange(term: string) {
        this.searchTerm = term;
        this.searchTerm$.next(term);
    }

    sortByColumn(column: string) {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }
        this.sortColumn$.next(this.sortColumn);
        this.sortDirection$.next(this.sortDirection);
    }

    resetSorting() {
        this.sortColumn = null;
        this.sortDirection = 'asc';
        this.sortColumn$.next(null);
        this.sortDirection$.next('asc');
    }

    trackByTag(index: number, item: BaseItem) {
        return item.tag;
    }

    goToDetail(item: BaseItem) {
        const routeMap: Record<string, string> = {
            ITEM: '/common-items/item-detail',
            ITEM_GROUPING: '/common-items/item-group-detail',
            MODIFIER_GROUPING: '/common-items/modifier-group-detail',
            MODIFIER: '/common-items/modifier-detail'
        };

        const itemClass = this.getItemClass(item);
        const route = routeMap[itemClass];

        if (route) {
            this.router.navigate([route, item.tag]);
        }
    }

    openCreateCommonItemDialog() {
        this.dialog.open(CreateCommonItemDialogComponent, {
            width: '750px',
            height: '600px',
        }).afterClosed().pipe().subscribe((result: {
            newItem: BaseItemRequestPayload | Modifier, requestedBy: string, class: string
        }) => {
            if (!result) {
                return;
            }

            console.log(result);

            if (result.class === 'MODIFIER') {
                this.createModifier(result);
            } else if (result.class === 'MODIFIER_GROUPING') {
                this.createModifierGroup(result);
            } else if (result.class === 'ITEM_GROUPING') {
                this.createItemGroup(result);
            } else {
                this.createItem(result);
            }
        });
    }

    private getAttribute(payload: BaseItemRequestPayload, jsonProperty: string): any {
        return payload.attributes?.find(attr => attr.jsonProperty === jsonProperty)?.value;
    }

    private handleCreateSuccess(tag: string, itemClass: string) {
        this.notificationService.success(`Successfully created ${tag}`);
        this.reloadItemsByClass(itemClass);
    }

    private handleCreateError(error: any) {
        this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
    }

    private createModifier(result: { newItem: BaseItemRequestPayload | Modifier, requestedBy: string, class: string }) {
        const payload = result.newItem as BaseItemRequestPayload;
        const modifier: Modifier = {
            tag: payload.tag,
            name: payload.name,
            image: payload.image,
            description: payload.description,
            modifierType: this.getAttribute(payload, 'modifierType'),
            type: payload.type,
            rmi: payload.rmi,
            disabled: true,
            locationSetTag: payload.locationSetTag,
            country: payload.country,
            isLocked: payload.isLocked,
            hideNutrition: this.getAttribute(payload, 'hideNutrition'),
            maximum: this.getAttribute(payload, 'maximum'),
        };
        this.itemDataService.addModifier(modifier, result.requestedBy).subscribe({
            next: (response) => {
                if (response) {
                    this.handleCreateSuccess(result.newItem.tag, result.class);
                }
            },
            error: (error) => this.handleCreateError(error)
        });
    }

    private createModifierGroup(result: { newItem: BaseItemRequestPayload | Modifier, requestedBy: string, class: string }) {
        const payload = result.newItem as BaseItemRequestPayload;
        const modifierGroup = {
            tag: payload.tag,
            name: payload.name,
            image: payload.image,
            description: payload.description,
            modifierTypeGrouping: this.getAttribute(payload, 'modifierTypeGrouping'),
            type: payload.type,
            locationSetTag: payload.locationSetTag,
            country: payload.country,
            maximum: this.getAttribute(payload, 'maximum'),
            components: payload.components || [],
            isLocked: payload.isLocked,
            disabled: true
        };
        this.itemDataService.addModifierGroup(modifierGroup, result.requestedBy).subscribe({
            next: (response) => {
                if (response) {
                    this.handleCreateSuccess(result.newItem.tag, result.class);
                }
            },
            error: (error) => this.handleCreateError(error)
        });
    }

    private createItem(result: { newItem: BaseItemRequestPayload | Modifier, requestedBy: string, class: string }) {
        this.itemDataService.createItem(result.newItem as BaseItemRequestPayload, result.requestedBy).subscribe({
            next: (response) => {
                if (response) {
                    this.handleCreateSuccess(result.newItem.tag, result.class);
                }
            },
            error: (error) => this.handleCreateError(error)
        });
    }

    private createItemGroup(result: { newItem: BaseItemRequestPayload | Modifier, requestedBy: string, class: string }) {
        const payload = result.newItem as BaseItemRequestPayload;
        const itemGroup: BaseItemRequestPayload = {
            tag: payload.tag,
            name: payload.name,
            image: payload.image,
            description: payload.description,
            type: payload.type,
            rmi: payload.rmi,
            locationSetTag: payload.locationSetTag,
            country: payload.country,
            isLocked: payload.isLocked,
            attributes: payload.attributes || [],
            components: payload.components || []
        };
        this.itemDataService.createItemGroup(itemGroup, result.requestedBy).subscribe({
            next: (response) => {
                if (response) {
                    this.handleCreateSuccess(result.newItem.tag, result.class);
                }
            },
            error: (error) => this.handleCreateError(error)
        });
    }
}
